// Focused lineage canvas.
//
// Lineage is a graph, so it is drawn as one: columns by medallion layer,
// nodes as cards, real bezier connectors between them. The earlier nested
// list was structurally correct and visually useless - you could not see
// the flow, which is the entire point.
//
// Two ideas carry the design:
//
//  1. FOCUS + CONTEXT. Rendering all 66 tables at once is a hairball. The
//     canvas instead shows the selected table plus everything that feeds it
//     and everything it feeds - typically 5-25 nodes. The selected card is
//     emphasised, its direct neighbours are solid, and anything further out
//     is dimmed, so depth reads without a legend.
//
//  2. LAYOUT BY MEANING. A node's column is its layer, never its distance
//     from the selection. Raw is always left, Decision-Ready always right,
//     so the same table sits in the same place no matter what you clicked.
//     Within a column, nodes are ordered by the average position of what
//     they connect to (barycenter), which removes most edge crossings.

import { useMemo, useState } from "react";
import type { LineageGraphNode } from "../types";

export interface CanvasNode extends LineageGraphNode {
  /** Distance from the focused node. 0 is the focus itself. */
  hops: number;
  direction: "up" | "down" | "self";
}

interface Props {
  nodes: CanvasNode[];
  edges: { from: string; to: string }[];
  focusId: string;
  onSelect: (id: string) => void;
  /** Fullscreen: drop the height cap so the whole graph is visible. */
  tall?: boolean;
  /** Layer key -> live run state, so a running layer reads at a glance. */
  runState?: Record<string, string>;
}

const COLUMNS = ["bronze", "silver", "gold"] as const;
const COL_LABEL: Record<string, string> = {
  bronze: "Raw Ingestion",
  silver: "Cleansing",
  gold: "Decision-Ready",
};

const COL_W = 186;
const COL_GAP = 78;
const NODE_H = 56;
const NODE_GAP = 16;
const PAD_Y = 40;
const PAD_X = 28;

interface Placed {
  node: CanvasNode;
  x: number;
  y: number;
  col: number;
}

export function LineageCanvas({
  nodes,
  edges,
  focusId,
  onSelect,
  tall,
  runState,
}: Props) {
  const [hover, setHover] = useState<string | null>(null);

  // Full ancestor + descendant closure of the hovered node, within the
  // rendered subgraph. Highlighting only the direct neighbours would answer
  // the wrong question: what matters on hover is the whole path a change
  // travels, not the first hop of it.
  const trace = useMemo(() => {
    if (!hover) return null;
    const up = new Map<string, string[]>();
    const down = new Map<string, string[]>();
    for (const e of edges) {
      (down.get(e.from) ?? down.set(e.from, []).get(e.from)!).push(e.to);
      (up.get(e.to) ?? up.set(e.to, []).get(e.to)!).push(e.from);
    }
    const reach = (adj: Map<string, string[]>) => {
      const seen = new Set<string>();
      const stack = [hover];
      while (stack.length) {
        const id = stack.pop()!;
        for (const nb of adj.get(id) ?? []) {
          if (seen.has(nb)) continue;
          seen.add(nb);
          stack.push(nb);
        }
      }
      return seen;
    };
    const ups = reach(up);
    const downs = reach(down);
    const all = new Set<string>([hover, ...ups, ...downs]);
    const wires = new Set<string>();
    for (const e of edges) {
      if (all.has(e.from) && all.has(e.to)) wires.add(`${e.from}->${e.to}`);
    }
    return { nodes: all, ups, downs, wires };
  }, [hover, edges]);

  const { placed, width, height, paths, colHeads } = useMemo(() => {
    // Column = layer, then a sub-column for depth WITHIN that layer.
    //
    // Intra-layer dependencies are real and common here: gold_orders feeds
    // gold_winback_performance, both Decision-Ready. Collapsing a layer to
    // one column would put them on top of each other and silently drop the
    // edge between them, which is exactly the kind of missing line that
    // makes a lineage view untrustworthy.
    const layerOf = new Map(nodes.map((n) => [n.id, n.layer]));
    const inLayerUp = new Map<string, string[]>();
    for (const e of edges) {
      if (layerOf.get(e.from) !== layerOf.get(e.to)) continue;
      const list = inLayerUp.get(e.to);
      if (list) list.push(e.from);
      else inLayerUp.set(e.to, [e.from]);
    }
    const subDepth = new Map<string, number>();
    const depthOf = (id: string, guard: Set<string>): number => {
      if (subDepth.has(id)) return subDepth.get(id)!;
      if (guard.has(id)) return 0;
      guard.add(id);
      const parents = inLayerUp.get(id) ?? [];
      const d = parents.length
        ? Math.max(...parents.map((p) => depthOf(p, guard) + 1))
        : 0;
      subDepth.set(id, d);
      return d;
    };
    for (const n of nodes) depthOf(n.id, new Set());

    // Sort key: layer index, then sub-depth. Gives a stable left-to-right
    // order where Raw is always leftmost regardless of what is selected.
    const keyOf = (n: CanvasNode) =>
      COLUMNS.indexOf(n.layer as (typeof COLUMNS)[number]) * 100 +
      (subDepth.get(n.id) ?? 0);

    const byKey = new Map<number, CanvasNode[]>();
    for (const n of nodes) {
      if (COLUMNS.indexOf(n.layer as (typeof COLUMNS)[number]) < 0) continue;
      const k = keyOf(n);
      const list = byKey.get(k);
      if (list) list.push(n);
      else byKey.set(k, [n]);
    }

    const keys = [...byKey.keys()].sort((a, b) => a - b);
    const upstreamOf = new Map<string, string[]>();
    for (const e of edges) {
      const list = upstreamOf.get(e.to);
      if (list) list.push(e.from);
      else upstreamOf.set(e.to, [e.from]);
    }

    // Barycenter ordering within each column removes most edge crossings.
    const rowOf = new Map<string, number>();
    for (const k of keys) {
      const list = byKey.get(k)!;
      list.sort((a, b) => {
        const ba = barycenter(a.id, upstreamOf, rowOf);
        const bb = barycenter(b.id, upstreamOf, rowOf);
        if (ba !== bb) return ba - bb;
        return a.label.localeCompare(b.label);
      });
      list.forEach((n, i) => rowOf.set(n.id, i));
    }

    const tallest = Math.max(1, ...keys.map((k) => byKey.get(k)!.length));
    const height = PAD_Y * 2 + tallest * NODE_H + (tallest - 1) * NODE_GAP;
    const width = PAD_X * 2 + keys.length * COL_W + Math.max(0, keys.length - 1) * COL_GAP;

    const placed: Placed[] = [];
    keys.forEach((k, ci) => {
      const list = byKey.get(k)!;
      const stackH = list.length * NODE_H + (list.length - 1) * NODE_GAP;
      const top = (height - stackH) / 2;
      list.forEach((n, i) => {
        placed.push({
          node: n,
          col: ci,
          x: PAD_X + ci * (COL_W + COL_GAP),
          y: top + i * (NODE_H + NODE_GAP),
        });
      });
    });

    // Label only the first sub-column of each layer, so "Decision-Ready"
    // is not repeated three times across its sub-columns.
    const colHeads: { x: number; layer: string }[] = [];
    let lastLayer = "";
    keys.forEach((k, ci) => {
      const layer = byKey.get(k)![0].layer;
      if (layer !== lastLayer) {
        colHeads.push({ x: PAD_X + ci * (COL_W + COL_GAP), layer });
        lastLayer = layer;
      }
    });

    const pos = new Map(placed.map((p) => [p.node.id, p]));
    const paths = edges
      .map((e) => {
        const a = pos.get(e.from);
        const b = pos.get(e.to);
        if (!a || !b || a.col === b.col) return null;
        const [l, r] = a.col < b.col ? [a, b] : [b, a];
        const x1 = l.x + COL_W;
        const y1 = l.y + NODE_H / 2;
        const x2 = r.x;
        const y2 = r.y + NODE_H / 2;
        const dx = Math.max(28, (x2 - x1) * 0.5);
        return {
          key: `${e.from}->${e.to}`,
          d: `M${x1},${y1} C${x1 + dx},${y1} ${x2 - dx},${y2} ${x2},${y2}`,
          active: e.from === focusId || e.to === focusId,
        };
      })
      .filter(Boolean) as { key: string; d: string; active: boolean }[];

    return { placed, width, height, paths, colHeads };
  }, [nodes, edges, focusId]);

  if (!placed.length) {
    return <div className="lcv-empty">Nothing to draw for this table.</div>;
  }

  return (
    <div className={tall ? "lcv-scroll is-tall" : "lcv-scroll"}>
      <div className="lcv" style={{ width, height }}>
        <svg className="lcv-wires" width={width} height={height} aria-hidden="true">
          {paths.map((p) => (
            <path
              key={p.key}
              d={p.d}
              className={[
                "lcv-wire",
                p.active && !trace ? "lcv-wire-on" : "",
                trace ? (trace.wires.has(p.key) ? "lcv-wire-trace" : "lcv-wire-mute") : "",
              ]
                .filter(Boolean)
                .join(" ")}
            />
          ))}
        </svg>

        {colHeads.map((c) => (
          <div key={c.layer} className="lcv-collabel" style={{ left: c.x, width: COL_W }}>
            <span className={`lcv-coldot lcv-coldot-${c.layer}`} />
            {COL_LABEL[c.layer] ?? c.layer}
          </div>
        ))}

        {placed.map(({ node, x, y }) => {
          const focused = node.id === focusId;
          const near = node.hops <= 1;
          return (
            <button
              key={node.id}
              className={[
                "lcv-card",
                `lcv-${node.layer}`,
                focused ? "is-focus" : "",
                near ? "is-near" : "is-far",
                node.internal ? "is-internal" : "",
                runState?.[node.layer] ? `is-${runState[node.layer]}` : "",
                trace
                  ? trace.nodes.has(node.id)
                    ? node.id === hover
                      ? "is-hover"
                      : trace.ups.has(node.id)
                        ? "is-tracedup"
                        : "is-traceddown"
                    : "is-muted"
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
              style={{ left: x, top: y, width: COL_W, height: NODE_H }}
              onMouseEnter={() => setHover(node.id)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(node.id)}
              onBlur={() => setHover(null)}
              onClick={() => onSelect(node.id)}
              aria-current={focused ? "true" : undefined}
              title={`${node.label} (${node.id})`}
            >
              <span className="lcv-card-label">{node.label}</span>
              <code className="lcv-card-name">{node.name}</code>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function barycenter(
  id: string,
  upstreamOf: Map<string, string[]>,
  rowOf: Map<string, number>,
): number {
  const ups = upstreamOf.get(id) ?? [];
  const rows = ups.map((u) => rowOf.get(u)).filter((r): r is number => r !== undefined);
  if (!rows.length) return Number.MAX_SAFE_INTEGER;
  return rows.reduce((a, b) => a + b, 0) / rows.length;
}
